import { useState } from 'react';
import { AlertTriangle, Loader2, Undo2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ReturnPlanView, ReturnResultView, type ReturnPlan, type ReturnResult } from '@/components/admin/ReturnBooking';
import { adminFetch } from '@/hooks/admin/useAdminApi';

/**
 * ReturnedPaymentPanel — booking of a returned/bounced payment that is NOT in
 * the Returned payments queue (QuickBooks Payments ACH return, bounced check).
 * Portal payments and Stripe disputes are booked from their payment ticket.
 *
 * Preview is read-only: it locates the applied QBO payment, every invoice it
 * paid (they all reopen — a bank pulls back the whole payment) and the deposit
 * bank account, and shows exactly what Record will do. Record books the return
 * the reconciliation-safe way (expense-swap: returned-payment expense on the
 * bank account, original payment re-applied to it, invoices reopen, deposit
 * stays reconciled), creates the $35 returned-payment fee invoice, pauses ordering
 * (policy: auto-hold on first return, 2026-07-09), and emails the customer's
 * invoice recipients. Backed by admin-record-returned-payment →
 * qbo-returned-payment v2 (apbg-billing edge function).
 */

const RETURN_REASONS = [
  'insufficient funds (R01)',
  'account closed (R02)',
  'payment stopped (R08)',
  'bounced check',
  'other',
];

function fmtUsd(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export default function ReturnedPaymentPanel({
  customerId,
  onRecorded,
}: {
  customerId: string;
  onRecorded: () => void;
}) {
  const [invoice, setInvoice] = useState('');
  const [reason, setReason] = useState(RETURN_REASONS[0]!);
  const [fee, setFee] = useState('35');
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [pauseOrdering, setPauseOrdering] = useState(true);
  const [busy, setBusy] = useState<'preview' | 'record' | null>(null);
  const [plan, setPlan] = useState<ReturnPlan | null>(null);
  const [result, setResult] = useState<ReturnResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const payload = () => ({
    customer_id: customerId,
    invoice: invoice.trim(),
    return_reason: reason,
    fee_amount: Number(fee) || 0,
    hold: pauseOrdering,
    notify: notifyCustomer,
  });

  async function runPreview() {
    setBusy('preview');
    setError(null);
    setResult(null);
    setPlan(null);
    try {
      const res = await adminFetch<{ plan: ReturnPlan }>('admin-record-returned-payment', {
        method: 'POST',
        body: JSON.stringify({ ...payload(), mode: 'preview' }),
      });
      setPlan(res.plan);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function runRecord() {
    if (!plan) return;
    if (
      !window.confirm(
        `Book returned payment ${plan.payment.id} (${fmtUsd(plan.payment.total)}) in QuickBooks — reopen ` +
          plan.reopens.map((i) => `#${i.doc_number}`).join(', ') +
          (Number(fee) > 0 ? `, and create a ${fmtUsd(Number(fee))} return-fee invoice?` : '?') +
          ' This writes to QuickBooks and cannot be undone from here.',
      )
    ) {
      return;
    }
    setBusy('record');
    setError(null);
    try {
      const res = await adminFetch<ReturnResult>('admin-record-returned-payment', {
        method: 'POST',
        // The preview listed every invoice that reopens; the person confirmed it.
        body: JSON.stringify({ ...payload(), mode: 'record', reopen_all: true }),
      });
      setResult(res);
      setPlan(null);
      setInvoice('');
      onRecorded();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-4 space-y-3">
      <div className="flex items-center gap-2">
        <Undo2 size={15} className="text-red-600" />
        <h2 className="font-display text-sm font-semibold text-brix-ink">Returned payment</h2>
      </div>
      <p className="text-[12px] text-brix-muted leading-relaxed">
        When a QuickBooks Payments ACH bounces (or a check is returned): this books it in
        QuickBooks the reconciliation-safe way — a returned-payment expense on your bank account
        (what the clawback withdrawal matches to in the bank feed) with the original payment
        re-applied to it, so every invoice it paid shows unpaid again and the deposit stays reconciled.
        It also bills the return fee as a separate invoice, pauses ordering, and emails the
        customer. Preview first — it shows exactly what will happen. A payment made on the
        portal (a Stripe dispute or returned ACH) is booked from its ticket under
        Payments → Returned payments instead.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
            Invoice # (from Intuit's return email)
          </label>
          <Input
            value={invoice}
            onChange={(e) => setInvoice(e.target.value)}
            placeholder="9989019999"
            className="mt-1"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
            Return reason
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1 w-full rounded-md border border-brix-border bg-brix-surface px-3 py-2 text-sm text-brix-ink"
          >
            {RETURN_REASONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
            Return fee ($)
          </label>
          <Input
            value={fee}
            onChange={(e) => setFee(e.target.value)}
            type="number"
            min={0}
            className="mt-1"
          />
        </div>
      </div>

      <div className="flex items-center gap-5 flex-wrap">
        <label className="flex items-center gap-2 text-xs text-brix-ink cursor-pointer">
          <input
            type="checkbox"
            checked={notifyCustomer}
            onChange={(e) => setNotifyCustomer(e.target.checked)}
            className="accent-brix-primary"
          />
          Email the customer
        </label>
        <label className="flex items-center gap-2 text-xs text-brix-ink cursor-pointer">
          <input
            type="checkbox"
            checked={pauseOrdering}
            onChange={(e) => setPauseOrdering(e.target.checked)}
            className="accent-brix-primary"
          />
          Pause ordering (credit hold)
        </label>
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void runPreview()}
          disabled={busy !== null || invoice.trim() === ''}
        >
          {busy === 'preview' ? <Loader2 size={12} className="animate-spin" /> : null}
          Preview
        </Button>
        {plan && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => void runRecord()}
            disabled={busy !== null}
            className="bg-red-600 hover:bg-red-700 text-red-50"
          >
            {busy === 'record' ? <Loader2 size={12} className="animate-spin" /> : null}
            Record in QuickBooks
          </Button>
        )}
      </div>

      {plan && <ReturnPlanView plan={plan} pauseOrdering={pauseOrdering} notifyCustomer={notifyCustomer} />}

      {result && <ReturnResultView result={result} />}

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-800">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
